import { CODE_AREA } from '../consts.js';

const SCHEMA_CODE_AREA_FILENAMES = {
  [CODE_AREA.BODY_SCHEMA]: 'body.json',
  [CODE_AREA.QUERY_SCHEMA]: 'query.json',
  [CODE_AREA.HEADERS_SCHEMA]: 'headers.json',
  [CODE_AREA.COOKIES_SCHEMA]: 'cookies.json',
  [CODE_AREA.ERROR_SCHEMA]: 'errorResponse.json',
  [CODE_AREA.SUCCESS_SCHEMA]: 'successResponse.json',
};

const SCHEMAS_SCOPE = 'schemas';
const SCRIPTS_SCOPE = 'scripts';

export function getCurrentPath(
  model,
  selectedCodeView,
  scriptIndex,
  internalVersionPath
) {
  const pieces = [model._id, internalVersionPath];

  if (selectedCodeView === CODE_AREA.SCRIPT) {
    pieces.push(SCRIPTS_SCOPE);

    if (scriptIndex !== void 0) {
      pieces.push(`${model.scripts[scriptIndex]._id}.js`);
    } else {
      pieces.push(`${model._id}.js`);
    }
  } else {
    pieces.push(SCHEMAS_SCOPE);
    pieces.push(SCHEMA_CODE_AREA_FILENAMES[selectedCodeView]);
  }

  return pieces.join('/');
}

function updateMonacoModelIntoEndpoint(endpointModel, monacoModel) {
  const path = monacoModel.uri.path;
  const value = monacoModel.getValue();

  if (!path) {
    return endpointModel;
  }

  const [_endpointId, _internalVersion, scope, file] = path
    .split('/')
    .filter(Boolean);

  if (!file) {
    return endpointModel;
  }

  const [fileName, _ext] = file.split('.');

  if (scope === SCHEMAS_SCOPE) {
    return {
      ...endpointModel,
      schemas: { ...endpointModel.schemas, [fileName]: value },
    };
  }

  if (scope === SCRIPTS_SCOPE) {
    if (Array.isArray(endpointModel.scripts)) {
      function findScriptIndex(script) {
        return script._id === fileName;
      }

      const newScripts = [...endpointModel.scripts];
      const index = newScripts.findIndex(findScriptIndex);

      if (index >= 0) {
        newScripts[index] = { ...newScripts[index], script: value };
      }

      return { ...endpointModel, scripts: newScripts };
    }

    return { ...endpointModel, script: value };
  }

  return endpointModel;
}

export function updateMonacoModelsIntoEndpoint(monacoModels, endpointModel) {
  return monacoModels.reduce(updateMonacoModelIntoEndpoint, endpointModel);
}

function getSchemaContent(schemas, monacoModel) {
  const path = monacoModel.uri.path;
  const value = monacoModel.getValue();

  if (!path) {
    return schemas;
  }

  const [_endpointId, _internalVersion, scope, file] = path
    .split('/')
    .filter(Boolean);

  if (!file) {
    return schemas;
  }

  const [fileName, _ext] = file.split('.');

  if (scope === SCHEMAS_SCOPE) {
    return { ...schemas, [fileName]: value };
  }

  return schemas;
}

function getSchemasFromMonaco(monacoModels) {
  return monacoModels.reduce(getSchemaContent, {});
}

export function getCurrentEndpointSchemas(endpointModel, monacoModels) {
  return { ...endpointModel.schemas, ...getSchemasFromMonaco(monacoModels) };
}

/**
 * Writes a copilot draft into the open Monaco models: the script model gets
 * `draft.script`, each `schemas/<key>.json` model gets `draft.schemas[key]`.
 * Models that are not open (a schema tab never selected) are left to the
 * endpoint model, which the editor reads as `defaultValue` when the tab opens.
 * Returns the paths it wrote, for the draft summary.
 */
const SCHEMA_CODE_AREAS = {
  query: CODE_AREA.QUERY_SCHEMA,
  body: CODE_AREA.BODY_SCHEMA,
  headers: CODE_AREA.HEADERS_SCHEMA,
  cookies: CODE_AREA.COOKIES_SCHEMA,
  successResponse: CODE_AREA.SUCCESS_SCHEMA,
  errorResponse: CODE_AREA.ERROR_SCHEMA,
};

/** Sidebar titles of the code views, for the copilot's tool results. */
export const CODE_AREA_LABELS = {
  [CODE_AREA.SCRIPT]: 'Script',
  [CODE_AREA.QUERY_SCHEMA]: 'Query Parameters',
  [CODE_AREA.BODY_SCHEMA]: 'Body',
  [CODE_AREA.HEADERS_SCHEMA]: 'Headers',
  [CODE_AREA.COOKIES_SCHEMA]: 'Cookies',
  [CODE_AREA.SUCCESS_SCHEMA]: 'Success response',
  [CODE_AREA.ERROR_SCHEMA]: 'Error response',
};

/**
 * The code view (sidebar tab) a draft should bring in front: the script when
 * the draft changes it, else the first changed schema, else null — a draft
 * that only touches settings leaves the view alone. Without this, "the draft
 * is in the editor" pointed at an editor still showing the query schema and
 * the user had to find the Script tab themselves.
 */
export function codeViewForDraft(before, draft) {
  const current = before || {};
  const next = draft || {};
  if (typeof next.script === 'string' && next.script !== current.script) {
    return CODE_AREA.SCRIPT;
  }
  const schemas = next.schemas || {};
  const currentSchemas = current.schemas || {};
  for (const key of Object.keys(SCHEMA_CODE_AREAS)) {
    if (typeof schemas[key] === 'string' && schemas[key] !== currentSchemas[key]) {
      return SCHEMA_CODE_AREAS[key];
    }
  }
  return null;
}

export function applyDraftToMonacoModels(monacoModels, draft) {
  const written = [];

  monacoModels.forEach(monacoModel => {
    const path = monacoModel.uri && monacoModel.uri.path;
    if (!path) {
      return;
    }
    const segments = path.split('/').filter(Boolean);
    const scope = segments[2];
    const file = segments[3];
    if (!file) {
      return;
    }
    const [fileName] = file.split('.');

    let value;
    if (scope === SCRIPTS_SCOPE && typeof draft.script === 'string') {
      value = draft.script;
    } else if (scope === SCHEMAS_SCOPE && draft.schemas && typeof draft.schemas[fileName] === 'string') {
      value = draft.schemas[fileName];
    }

    if (value !== undefined && monacoModel.getValue() !== value) {
      monacoModel.setValue(value);
      written.push(path);
    }
  });

  return written;
}
